import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React from 'react'
import { useSelector } from 'react-redux'
import CheckFilledLoD from 'src/assets/svgs/CheckFilledLoD.svg'
import Sparkles from 'src/assets/svgs/Sparkles.svg'
import Spotlight from 'src/components/Spotlight/Spotlight'
import TestId from 'src/components/TestId/TestId'
import { useSlidingNavBar } from 'src/hooks'
import Lnk from 'src/navigation/Lnk'
import { LocationState } from 'src/redux/createRootReducer'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'
import styled, { css, CSSProp } from 'styled-components'

export enum SubnavDesign {
  Header = 'header',
  Box = 'box',
  Floating = 'floating',
}

const BottomBar = styled.div`
  position: absolute;
  bottom: 0;
  height: 2px;
  background-color: ${themeConstants.colors.cta};
`

const LinkContainerStyle = (
  isActive: boolean,
  isNegative?: boolean,
): CSSProp => css`
  white-space: nowrap;
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  ${getFontCss(
    14,
    styleConstants.colors.gray_dark,
    styleConstants.fontFamilies.medium,
  )}
  cursor: pointer;

  &:nth-child(n + 2) {
    margin-left: 36px;
  }

  ${isActive &&
  css`
    color: ${themeConstants.colors.cta};
  `}

  &:hover {
    color: ${themeConstants.colors.cta};
  }

  ${isNegative &&
  css`
    color: ${styleConstants.colors.red};
    &:hover {
      color: ${styleConstants.colors.red_dark};
    }
  `}
`

export type SubnavOption = SubnavOptionAriaSource | SubnavOptionAriaLabel

interface SubnavOptionShared {
  label: string | React.ReactElement
  to?: string | Location<LocationState>
  onClick?: () => void
  isActive?: boolean
  hasTooltip?: boolean
  setTooltip?: (val: boolean) => void
  tooltipTitle?: React.ReactNode
  tooltipText?: React.ReactNode
  id?: string
  sparkle?: boolean
  preventSetTooltipOnClickTab?: boolean
  'data-testid'?: string
  hasNegativeStyle?: boolean
}

interface SubnavOptionAriaSource extends SubnavOptionShared {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface SubnavOptionAriaLabel extends SubnavOptionShared {
  ariaLabel: string
  ariaLabelSource?: undefined
}

interface SubnavProps {
  className?: string
  options: SubnavOption[]
  negativeOptions?: SubnavOption[]
  design: SubnavDesign
}

const Subnav: React.FC<SubnavProps> = (props): React.ReactElement => {
  const selectedIndex = props.options.reduce(
    (acc: number | undefined, curr, i) => (curr.isActive ? i : acc),
    undefined,
  )

  const { barStyle, showStaticBars, containerDivRef } = useSlidingNavBar(
    selectedIndex,
    (el: HTMLElement) => ({
      left: el.offsetLeft,
      width: el.scrollWidth,
    }),
  )

  const fixedElementPushdown = useSelector(selectFixedElementPushdown)

  return (
    <div
      className={props.className}
      css={css`
        ${props.design !== SubnavDesign.Floating &&
        css`
          background-color: ${styleConstants.colors.white};
        `}
        display: flex;
        height: 10px;
        justify-content: space-between;
        overflow: auto hidden;

        ${props.options.length > 0 &&
        css`
          min-height: 50px;
          max-height: 50px;
        `}

        ${props.design === SubnavDesign.Header &&
        css`
          position: sticky;
          z-index: 2;
          box-shadow: 0 5px 10px -7px rgba(0, 0, 0, 0.15);

          top: ${fixedElementPushdown +
          Number(styleConstants.heights.topNavSMUp.replace('px', ''))}px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            top: ${fixedElementPushdown +
            Number(styleConstants.heights.topNavXSMDown.replace('px', ''))}px;
          `)}
        `}

        ${props.design === SubnavDesign.Box &&
        css`
          padding: 0px ${styleConstants.widths.cardPaddingXSMUp};
          border-bottom: 2px solid ${styleConstants.colors.gray_light};
          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            padding: 0px ${styleConstants.widths.cardPaddingXXSMOnly};
          `)}
        `}

        ${props.design === SubnavDesign.Header &&
        css`
          ${getBreakpointUp(breakpointSizes.LG)(css`
            padding-left: ${styleConstants.widths.headerPaddingLGOnly};
          `)}
          ${getBreakpointOnly(breakpointSizes.MD)(css`
            padding-left: ${styleConstants.widths.headerPaddingMDOnly};
          `)}
          ${getBreakpointOnly(breakpointSizes.SM)(css`
            padding-left: ${styleConstants.widths.headerPaddingSMOnly};
          `)}
          ${getBreakpointOnly(breakpointSizes.XSM)(css`
            padding-left: ${styleConstants.widths.headerPaddingXSMOnly};
          `)}
          ${getBreakpointDown(breakpointSizes.XXSM)(css`
            padding-left: ${styleConstants.widths.headerPaddingXXSMOnly};
          `)}
        `}

        ${props.negativeOptions !== undefined &&
        props.design === SubnavDesign.Header &&
        css`
          ${getBreakpointUp(breakpointSizes.LG)(css`
            padding-right: ${styleConstants.widths.headerPaddingLGOnly};
          `)}
          ${getBreakpointOnly(breakpointSizes.MD)(css`
            padding-right: ${styleConstants.widths.headerPaddingMDOnly};
          `)}
          ${getBreakpointOnly(breakpointSizes.SM)(css`
            padding-right: ${styleConstants.widths.headerPaddingSMOnly};
          `)}
          ${getBreakpointOnly(breakpointSizes.XSM)(css`
            padding-right: ${styleConstants.widths.headerPaddingXSMOnly};
          `)}
          ${getBreakpointDown(breakpointSizes.XXSM)(css`
            padding-right: ${styleConstants.widths.headerPaddingXXSMOnly};
          `)}
        `}
      `}
    >
      <div
        ref={containerDivRef}
        className={'positive-options'}
        css={css`
          display: flex;
          position: relative;
        `}
      >
        {props.options.map((option, i) =>
          option.to !== undefined ? (
            <div
              key={i}
              css={css`
                white-space: nowrap;
                position: relative;
                height: 100%;
                display: flex;
                align-items: center;

                &:nth-child(n + 2) {
                  margin-left: 36px;
                }
              `}
            >
              <TestId
                testId={option['data-testid'] ? option['data-testid'] : null}
                key={i}
              >
                <Lnk
                  id={option.id}
                  to={option.to}
                  {...(option.ariaLabelSource !== undefined
                    ? { ariaLabelSource: option.ariaLabelSource }
                    : { ariaLabel: option.ariaLabel })}
                  onClick={() => {
                    if (
                      option.setTooltip !== undefined &&
                      !option.preventSetTooltipOnClickTab
                    ) {
                      option.setTooltip(false)
                    }
                    option.onClick?.()
                  }}
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_dark,
                      styleConstants.fontFamilies.medium,
                    )}
                    cursor: pointer;

                    ${!!option.isActive &&
                    css`
                      color: ${themeConstants.colors.cta};
                    `}

                    &:hover {
                      color: ${themeConstants.colors.cta};
                    }
                  `}
                >
                  {option.label}
                  {showStaticBars && option.isActive && <BottomBar />}
                </Lnk>
                {option.hasTooltip && option.setTooltip && (
                  <Spotlight
                    imageSrc={option.sparkle ? Sparkles : CheckFilledLoD}
                    tooltip
                    smallerText
                    title={option.tooltipTitle || <></>}
                    subtitle={option.tooltipText || <></>}
                    cookieFunction={option.setTooltip}
                    css={css`
                      position: absolute;
                      z-index: 1;
                      width: 300%;
                      top: 60px;
                    `}
                  />
                )}
              </TestId>
            </div>
          ) : (
            <div
              id={option.id ? option.id : 'posOptionTab'}
              onClick={option.onClick}
              onKeyPress={option.onClick}
              role={option.onClick ? 'button' : undefined}
              tabIndex={option.onClick ? 0 : undefined}
              key={i}
              css={LinkContainerStyle(!!option.isActive)}
            >
              <TestId
                testId={option['data-testid'] ? option['data-testid'] : null}
                key={i}
              >
                {option.label}
                {showStaticBars && option.isActive && <BottomBar />}
              </TestId>
            </div>
          ),
        )}
        {!showStaticBars && barStyle && (
          <div
            css={css`
              position: absolute;
              bottom: 0;
              height: 2px;
              width: 50%;
              background-color: ${themeConstants.colors.cta};
              transition:
                width ${styleConstants.transitionSpeed.slow} ease-out,
                left ${styleConstants.transitionSpeed.slowest} ease-out;
            `}
            style={{
              ...barStyle,
              width:
                Number(barStyle.width) > 250
                  ? +(barStyle.width ?? 0) / 3
                  : barStyle.width,
            }}
          />
        )}
      </div>
      {props.negativeOptions !== undefined && (
        <div
          css={css`
            margin-left: 36px;
          `}
        >
          {props.negativeOptions.map((option, i) =>
            option.to !== undefined ? (
              <Lnk
                id={option.id}
                to={option.to}
                onClick={option.onClick}
                key={i}
                css={LinkContainerStyle(
                  !!option.isActive,
                  option.hasNegativeStyle !== undefined
                    ? option.hasNegativeStyle
                    : true,
                )}
                {...(option.ariaLabelSource !== undefined
                  ? { ariaLabelSource: option.ariaLabelSource }
                  : { ariaLabel: option.ariaLabel })}
              >
                {option.label}
              </Lnk>
            ) : (
              <div
                id={option.id ? option.id : 'negOptionTab'}
                onClick={option.onClick}
                onKeyPress={option.onClick}
                role={option.onClick ? 'button' : undefined}
                tabIndex={option.onClick ? 0 : undefined}
                key={i}
                css={LinkContainerStyle(
                  !!option.isActive,
                  option.hasNegativeStyle !== undefined
                    ? option.hasNegativeStyle
                    : true,
                )}
              >
                {option.label}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  )
}

export default Subnav
